import "assets/css/reset.css";
import "assets/fonts/fonts.css";
import "assets/css/core.css";
import "assets/css/fonts.css";

import * as Sentry from "@sentry/react";
import {ErrorMessage} from "components/ErrorMessage";
import {GameErrors} from "components/GameErrorMessage";
import {HOCCountryCheck} from "components/HOCCountryCheck";
import {HOCDebugInformation} from "components/HOCDebugInformation";
import {HOCEntryPoint} from "components/HOCEntryPoint";
import {HOCi18n} from "components/HOCi18n";
import {HOCSession} from "components/HOCSession";
import {NotificationBanner} from "components/NotificationBanner";
import {TutorialModal} from "components/TutorialModal";
import {TutorialSecondModal} from "components/TutorialSecondModal";

import {store} from "modules/store";
import MulliganPage from "pages/homePage/MulliganPage";
import React from "react";
import {Provider} from "react-redux";
import {BrowserRouter} from "react-router-dom";
import styled from "@emotion/styled";
import {createRoot} from "react-dom/client";
import Routes from "routes";
import {ThemeProvider} from "@mui/material";
import {theme} from "assets/css/theme";
import Header from "components/Header";
import {HOCLiveScore} from "components/HOCLiveScore";
import {LiveScoreSection} from "modules/sagas/live_score";
import {HOCSecondarySecretKey, HOCSecretKey} from "components/HOCSecretKey";
import {HOCNavigate} from "components/HOCNavigate";
import {PromptModal} from "components/PromptModal";
import {RewardModal} from "components/RewardModal";
import {breadcrumbsIntegration, browserTracingIntegration} from "@sentry/react";

let errorCount = 0;
const MAX_ERRORS = 100;
const IGNORE_ERRORS = [
	"Authentication Required",
	"Load failed",
	"UET is not defined",
	"Loading chunk",
	"AbortError: Share canceled",
	"Failed to fetch",
	"Network Error",
	"Minified Redux error",
	"Unexpected end of script",
	"Abort due to cancellation of share",
	"The network connection was lost.",
	"Maximum call stack size exceeded",
	"Possible side-effect in debug-evaluate",
	"Native bridge was not set",
	"Initialization Error: Native bridge was not set",
	"UnhandledRejection: Non-Error promise rejection captured with keys: message, status",
	"Non-Error promise rejection captured with keys: message, status",
	"TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
	"Identifier MlbJavascriptDataStore has already been declared",
	"Identifier 'MlbJavascriptDataStore' has already been declared",
	"SyntaxError: Identifier MlbJavascriptDataStore has already been declared",
	"SyntaxError: Identifier 'MlbJavascriptDataStore' has already been declared",
	"Java exception was raised during method invocation",
	"Object captured as promise rejection with keys: message, status",
	"UnhandledRejection: Object captured as promise rejection with keys: message, status",
];

Sentry.init({
	dsn: "https://dd3fc69533bf4d91833d91cb8e0d3932@o151969.ingest.sentry.io/6008981",
	environment: process.env.REACT_APP_SENTRY_ENV || "development",
	sampleRate: 0.05,
	ignoreErrors: IGNORE_ERRORS,
	release: "0.0.3",
	allowUrls: [".loc", ".fanhubmedia.com", ".geniussports.com", "www.mlb.com", ".mlb.com"],
	integrations: [
		browserTracingIntegration(),
		breadcrumbsIntegration({
			console: false,
		}),
	],
	beforeSend: (event: Sentry.Event) => {
		errorCount++;
		if (errorCount > MAX_ERRORS) {
			return null;
		}
		if (event && event.message && IGNORE_ERRORS.includes(event.message)) {
			return null;
		}
		return event;
	},
	denyUrls: [
		"quantcast",
		"xsca",
		"https://people.api.boomtrain.com",
		"https://events.api.boomtrain.com/event/track",
		"https://b.clarity.ms/collect",
		// browser's extensions
		/extensions\//i,
		/^chrome:\/\//i,
		/^moz-extension:\/\//i,
		/api\.boomtrain\.com/i,
	],
});

const Content = styled.div`
	display: flex;
	flex-flow: column;

	@media (min-width: 879px) {
		flex: 1;
		overflow: hidden;
		height: 100%;
		height: -webkit-fill-available;
	}
`;

const container = document.getElementById("root");

if (container) {
	const root = createRoot(container);
	root.render(
		<React.StrictMode>
			<Provider store={store}>
				<ThemeProvider theme={theme}>
					<BrowserRouter basename={process.env.REACT_APP_BASE_URL}>
						<HOCDebugInformation>
							<HOCi18n>
								<HOCSecretKey>
									<HOCSecondarySecretKey>
										<HOCLiveScore section={LiveScoreSection.home}>
											<HOCCountryCheck>
												<HOCNavigate>
													<Content>
														<HOCSession>
															<Header />
															<NotificationBanner />
															<HOCEntryPoint>
																<Routes />
															</HOCEntryPoint>
														</HOCSession>
													</Content>
												</HOCNavigate>
												<ErrorMessage />
											</HOCCountryCheck>
											<GameErrors />
											<MulliganPage />
											<TutorialModal />
											<RewardModal />
											<TutorialSecondModal />
											<PromptModal />
										</HOCLiveScore>
									</HOCSecondarySecretKey>
								</HOCSecretKey>
							</HOCi18n>
						</HOCDebugInformation>
					</BrowserRouter>
				</ThemeProvider>
			</Provider>
		</React.StrictMode>
	);
}
